import { useState, useEffect, useCallback } from 'react';
import {
  getSessionTypeDetailApi,
  getCampByIdApi,
  getSchedulingBlocksApi,
  getSchedulededDatesApi,
} from '../../../util/api';

/**
 * Fetches ONE session type by its own Mongo _id (not the coach's shared
 * shell listingId, which every session type for a coach carries
 * identically -- see SessionTypeDetailPage.js's own comment for why that
 * distinction matters) and its real availability data, in the same shape
 * useListingSessionTypes.js already assembles for CustomBookingPanel, just
 * scoped to a single session instead of every session on a listing.
 */
export const useSessionTypeDetail = (sessionTypeId) => {
  const [sessionType, setSessionType] = useState(null);
  const [coachTimezone, setCoachTimezone] = useState(null);
  const [availabilityData, setAvailabilityData] = useState({
    camps: {},
    schedulingBlocks: {},
    scheduledDates: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sessionTypeId) {
      setSessionType(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getSessionTypeDetailApi({ sessionTypeId });
      const st = response?.data || null;
      setSessionType(st);
      setCoachTimezone(response?.coachTimezone || null);

      if (!st) {
        setAvailabilityData({ camps: {}, schedulingBlocks: {}, scheduledDates: [] });
        return;
      }

      const campsMap = {};
      if (st.isCampSession && st.campId) {
        try {
          const campResponse = await getCampByIdApi({ campId: st.campId, userId: st.userId });
          if (campResponse) {
            campsMap[st.campId] = campResponse;
          }
        } catch (err) {
          console.error(`Error loading camp ${st.campId}:`, err);
        }
      }

      let schedulingBlocks = {};
      let scheduledDates = [];
      if (!st.isCampSession) {
        try {
          const [blocksResponse, datesResponse] = await Promise.all([
            getSchedulingBlocksApi({ userId: st.userId }),
            getSchedulededDatesApi({ userId: st.userId }),
          ]);
          const blocks = Array.isArray(blocksResponse) ? blocksResponse : [];
          scheduledDates = Array.isArray(datesResponse) ? datesResponse : [];
          blocks.forEach((block) => {
            schedulingBlocks[block._id] = block;
          });
        } catch (err) {
          console.error('Error loading availability blocks:', err);
        }
      }

      setAvailabilityData({ camps: campsMap, schedulingBlocks, scheduledDates });
    } catch (err) {
      console.error('Error loading session type detail:', err);
      setError(err);
      setSessionType(null);
    } finally {
      setLoading(false);
    }
  }, [sessionTypeId]);

  useEffect(() => {
    load();
  }, [load]);

  return { sessionType, coachTimezone, availabilityData, loading, error, reload: load };
};

export default useSessionTypeDetail;
